import { useEffect, useRef, useState } from 'react';

import {
  createFunctionalTest,
  deleteFunctionalLibrary,
  deleteFunctionalTest,
  listFunctionalLibrary,
  loadSampleFunctionalLibrary,
  uploadFunctionalLibrary,
  type FunctionalLibraryItem,
  type FunctionalLibraryUploadResult,
} from '../../../api/functionalLibraryApi';
import { isSampleOnlyLibrary } from '../../onboarding/utils/onboardingDerivation';

export type FunctionalTest = FunctionalLibraryItem;
export type UploadResult = FunctionalLibraryUploadResult;

export const useFunctionalLibrary = (projectId: string) => {
  const [library, setLibrary] = useState<{ projectId: string; rows: FunctionalTest[] }>({ projectId, rows: [] });
  const rows = library.projectId === projectId ? library.rows : [];
  const activeProject = useRef(projectId);
  activeProject.current = projectId;
  const requestVersion = useRef(0);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResult | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const [newTestId, setNewTestId] = useState('');
  const [newName, setNewName] = useState('');
  const [newService, setNewService] = useState('');
  const [newSuite, setNewSuite] = useState('default');
  const [newCriticality, setNewCriticality] = useState('medium');
  const [newTags, setNewTags] = useState('');
  const [newDuration, setNewDuration] = useState('');

  const fetchTests = async () => {
    if (!projectId || activeProject.current !== projectId) return;
    const version = ++requestVersion.current;
    const isCurrent = () => activeProject.current === projectId && requestVersion.current === version;
    setLoading(true);

    try {
      const data = await listFunctionalLibrary(projectId);
      if (isCurrent()) setLibrary({ projectId, rows: data.data || [] });
    } catch (err) {
      console.error('Error fetching functional tests:', err);
      if (isCurrent()) setSnackbar('Failed to load functional tests');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedRows([]);
    setDeleteId(null);
    setConfirmOpen(false);
    setConfirmAllOpen(false);
    setSnackbar(null);
    if (!projectId) { setLoading(false); return; }
    void fetchTests();
  }, [projectId]);

  const sampleOnlyLibrary = isSampleOnlyLibrary(rows);

  const deleteTests = async (id?: number) => {
    try {
      const idsToDelete = id ? [id] : selectedRows;

      await Promise.all(
        idsToDelete.map((testId) => deleteFunctionalTest(projectId, testId))
      );

      setSnackbar(
        `${idsToDelete.length} test${idsToDelete.length !== 1 ? 's' : ''} deleted`
      );
      setSelectedRows([]);
      void fetchTests();
    } catch {
      setSnackbar('Failed to delete test(s)');
    }
  };

  const deleteAllTests = async () => {
    try {
      await deleteFunctionalLibrary(projectId);
      setSnackbar('All tests deleted');
      void fetchTests();
    } catch {
      setSnackbar('Failed to delete tests');
    }
  };

  const resetForm = () => {
    setNewTestId('');
    setNewName('');
    setNewService('');
    setNewSuite('default');
    setNewCriticality('medium');
    setNewTags('');
    setNewDuration('');
  };

  const addTest = async () => {
    if (!newTestId.trim() || !newName.trim() || !newService.trim()) {
      setSnackbar('Test ID, Name, and Service are required');
      return;
    }

    try {
      await createFunctionalTest(projectId, {
        test_id: newTestId.trim(),
        name: newName.trim(),
        service: newService.trim(),
        suite: newSuite.trim() || 'default',
        business_criticality: newCriticality,
        tags: newTags
          ? newTags.split(',').map((tag) => tag.trim()).filter(Boolean)
          : [],
        avg_duration_ms: newDuration ? parseInt(newDuration) : null,
      });

      setAddOpen(false);
      resetForm();
      setSnackbar('Test added');
      void fetchTests();
    } catch (err: any) {
      if (err.message?.includes('409')) {
        setSnackbar('Test ID already exists');
      } else {
        setSnackbar('Failed to add test');
      }
    }
  };

  const uploadTests = async (records: unknown[]) => {
    try {
      const result = await uploadFunctionalLibrary(projectId, records);

      setUploadResults(result);
      setUploadDialogOpen(true);
      void fetchTests();
    } catch (err) {
      console.error('Upload error:', err);
      setSnackbar('Upload failed. Compare your file with the template and format guide, then try again.');
    }
  };

  const loadSampleLibrary = async () => {
    try {
      await loadSampleFunctionalLibrary(projectId);
      setSnackbar('Sample test library loaded');
      void fetchTests();
    } catch {
      setSnackbar('Failed to load sample test library');
    }
  };

  const confirmDelete = async () => {
    if (deleteId !== null) {
      await deleteTests(deleteId);
      setConfirmOpen(false);
      setDeleteId(null);
    } else if (selectedRows.length > 0) {
      await deleteTests();
      setConfirmOpen(false);
    }
  };

  return {
    rows,
    snackbar,
    setSnackbar,
    loading,
    addOpen,
    setAddOpen,
    confirmOpen,
    setConfirmOpen,
    confirmAllOpen,
    setConfirmAllOpen,
    deleteId,
    setDeleteId,
    uploadResults,
    uploadDialogOpen,
    setUploadDialogOpen,
    pageSize,
    setPageSize,
    selectedRows,
    setSelectedRows,
    newTestId,
    setNewTestId,
    newName,
    setNewName,
    newService,
    setNewService,
    newSuite,
    setNewSuite,
    newCriticality,
    setNewCriticality,
    newTags,
    setNewTags,
    newDuration,
    setNewDuration,
    sampleOnlyLibrary,
    fetchTests,
    deleteTests,
    deleteAllTests,
    addTest,
    resetForm,
    uploadTests,
    loadSampleLibrary,
    confirmDelete,
  };
};
